import React, { useState } from 'react';
import './SinglePdfUpload.css';

const AI_PROVIDERS = [
  { id: 'claude', name: 'Claude' },
  { id: 'openai', name: 'GPT' },
  { id: 'gemini', name: 'Gemini' }
];

function ResidentialRefiUpload({ onFileUpload, onBack }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [emailInstructions, setEmailInstructions] = useState('');
  const [selectedModels, setSelectedModels] = useState(['claude', 'openai', 'gemini']);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [enableDataTrace, setEnableDataTrace] = useState(true); // Enable DataTrace by default

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        setUploadedFile(file);
      } else {
        alert('Please upload a PDF file');
      }
    }
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        setUploadedFile(file);
      } else {
        alert('Please upload a PDF file');
      }
    }
  };

  const handleModelToggle = (modelId) => {
    setSelectedModels(prev => {
      if (prev.includes(modelId)) {
        // Don't allow deselecting if it's the last one
        if (prev.length === 1) {
          alert('At least one AI model must be selected');
          return prev;
        }
        return prev.filter(id => id !== modelId);
      } else {
        return [...prev, modelId];
      }
    });
  };

  const handleAnalyze = async () => {
    if (!uploadedFile) return;

    setIsAnalyzing(true);
    try {
      // Pass file, selected models, instructions, and DataTrace setting to parent
      await onFileUpload(uploadedFile, {
        models: selectedModels,
        instructions: emailInstructions,
        enableDataTrace: enableDataTrace
      });
    } catch (error) {
      console.error('Analysis failed:', error);
      alert('Failed to analyze PDF. Please try again.');
      setIsAnalyzing(false);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  return (
    <div className="single-pdf-upload">
      <div className="upload-header">
        <div>
          <h2>Residential Refinance Order</h2>
          <p className="upload-subtitle">Upload residential refinance documents to automatically extract and pre-fill title order information</p>
        </div>
      </div>

      {/* PDF Upload Section */}
      <div
        className={`drop-zone ${isDragging ? 'dragging' : ''} ${uploadedFile ? 'has-file' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="drop-zone-content">
          {uploadedFile ? (
            <>
              <div className="upload-icon">📄</div>
              <h3>{uploadedFile.name}</h3>
              <p className="drop-zone-subtitle">PDF uploaded successfully</p>
              <button
                type="button"
                className="remove-file-button"
                onClick={handleRemoveFile}
                disabled={isAnalyzing}
              >
                Remove File
              </button>
            </>
          ) : (
            <>
              <div className="upload-icon">🏦</div>
              <h3>Drag and drop your refinance documents here</h3>
              <p className="drop-zone-subtitle">or</p>
              <label htmlFor="refi-file-input" className="upload-button">
                Browse Files
              </label>
              <input
                id="refi-file-input"
                type="file"
                accept=".pdf"
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
              <p className="file-info">Accepts PDF files only</p>
            </>
          )}
        </div>
      </div>

      {/* Email/Special Instructions Section */}
      <div className="refi-instructions-section">
        <h3>Email Contents / Special Instructions</h3>
        <p className="field-subtitle">Copy any email contents or special instructions for this order</p>
        <textarea
          className="instructions-textarea"
          value={emailInstructions}
          onChange={(e) => setEmailInstructions(e.target.value)}
          placeholder="Paste email contents or add special instructions here..."
          rows={6}
          disabled={isAnalyzing}
        />
      </div>

      {/* Analyze Button */}
      <div className="refi-analyze-section">
        <button
          className="analyze-button"
          onClick={handleAnalyze}
          disabled={!uploadedFile || isAnalyzing}
        >
          {isAnalyzing ? 'Analyzing with AI Models...' : 'Analyze Document'}
        </button>
      </div>

      {/* AI Model Selector Section */}
      <div className="refi-model-selector-section">
        <h3>Consensus AI Models</h3>
        <p className="field-subtitle">Select which AI models to use for analysis (at least one required)</p>
        <div className="model-checkboxes">
          {AI_PROVIDERS.map(provider => (
            <label key={provider.id} className="model-checkbox-label">
              <input
                type="checkbox"
                checked={selectedModels.includes(provider.id)}
                onChange={() => handleModelToggle(provider.id)}
                disabled={isAnalyzing}
              />
              <span className="model-name">{provider.name}</span>
            </label>
          ))}
        </div>
      </div>

      {/* DataTrace Auto-Analysis Toggle */}
      <div className="refi-model-selector-section" style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
        <h3>DataTrace Property Analysis</h3>
        <p className="field-subtitle">Automatically verify property information against public records</p>
        <label className="model-checkbox-label" style={{ fontSize: '1rem', fontWeight: '500' }}>
          <input
            type="checkbox"
            checked={enableDataTrace}
            onChange={() => setEnableDataTrace(!enableDataTrace)}
            disabled={isAnalyzing}
          />
          <span className="model-name">Enable automatic DataTrace analysis</span>
        </label>
      </div>
    </div>
  );
}

export default ResidentialRefiUpload;
